// src/services/api.js
import axios from "axios";
import { API_BASE_URL } from "../utils/constants";
import { reportClientError } from "../utils/debugErrorLog";

// Get token from localStorage - unified with frontend-thailand pattern
const getToken = () => {
  return localStorage.getItem("token") || "";
};

// Get active region from localStorage (set via region switcher in user menu)
const getRegion = () => {
  return localStorage.getItem("user_region") || "vn";
};

// Default headers including authorization and region/language headers
const getHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
  "x-region": getRegion(),
  "accept-language": "th",
});

// Create axios instance with baseUrl and default headers
// v68.306 — export api too so new screens can use the interceptor-installed
// instance (token / 401 / retry) instead of building their own headers.
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: getHeaders(),
  timeout: 60000, // Default 60 second timeout
});

// Interceptor to add token to all requests (ensures latest token used)
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always refresh region on each request (user may switch region after init)
    config.headers['x-region'] = getRegion();

    // ✅ FormData uploads: remove the default Content-Type so axios auto-sets
    // multipart/form-data WITH the correct boundary. Without this, the instance
    // default "application/json" header overrides it and multer can't parse the body.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // ✅ Debug logging for onboard-from-google endpoint
    if (config.url?.includes('onboard-from-google')) {
      console.log('🔍 Axios Interceptor - Request Details:', {
        url: config.url,
        method: config.method,
        data: config.data,
        dataType: typeof config.data,
        dataKeys: config.data ? Object.keys(config.data) : [],
        googlePlaceId: config.data?.googlePlaceId,
        name: config.data?.name,
        headers: config.headers
      });
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor to handle 401 Unauthorized responses (token expired or invalid)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // ✅ Enhanced error logging
    if (error.config?.url?.includes('onboard-from-google')) {
      console.error('❌ Axios Interceptor - Response Error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        requestData: error.config?.data,
        url: error.config?.url
      });
    }

    if (error.response?.status === 401) {
      // In dev mode with mock token, don't redirect (prevents infinite loop)
      const currentToken = localStorage.getItem("token") || "";
      if (process.env.NODE_ENV === 'development' && currentToken.startsWith('dev-mock-token-')) {
        console.warn('⚠️ Dev mode: Backend rejected mock token (401). Real JWT required for API calls.');
      } else {
        // Clear all auth-related keys (unified pattern)
        localStorage.removeItem("token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_role");
        localStorage.removeItem("user_region");
        localStorage.removeItem("user_email");
        localStorage.removeItem("user_phone");
        localStorage.removeItem("user_name");
        localStorage.removeItem("user_login_method");
        window.location.href = "/";
      }
    } else {
      // 2026-06-28 — surface the REAL error on screen + log to Neon (source='admin').
      try {
        const status = error.response?.status;
        const isNetwork = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
        const isTimeout = error.code === 'ECONNABORTED';
        const serverMsg = error.response?.data?.message || error.response?.data?.error;
        reportClientError({
          kind: isNetwork ? 'network' : (isTimeout ? 'timeout' : 'http'),
          name: error.name,
          message: serverMsg || error.message || 'Request failed',
          status,
          method: error.config?.method?.toUpperCase(),
          endpoint: error.config?.url,
          stack: error.stack,
        });
      } catch { /* never cascade */ }
    }
    return Promise.reject(error);
  }
);

// ✅ PRODUCTION: Enhanced retry helper with server error support
const retryRequest = async (requestFn, maxRetries = 2, delayMs = 2000) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt === maxRetries;

      // Retry on network errors, timeouts, and server errors
      const isNetworkError = error.code === "ERR_NETWORK" || error.message === "Network Error";
      const isTimeout = error.code === "ECONNABORTED";
      const isServerError = error.response?.status >= 500;
      const shouldRetry = (isNetworkError || isTimeout || isServerError) && !isLastAttempt;

      if (!shouldRetry) {
        throw error;
      }

      console.log(`🔄 Retry attempt ${attempt + 1}/${maxRetries} in ${delayMs}ms...`, {
        error: error.message,
        code: error.code,
        status: error.response?.status
      });

      await new Promise((resolve) => setTimeout(resolve, delayMs));
      delayMs *= 2; // Exponential backoff
    }
  }

  throw lastError;
};

/**
 * Extract user-friendly error message from API error
 * @param {Error} error - Axios error object
 * @returns {Object} - { message: string, code: string, details: any }
 */
const extractErrorDetails = (error) => {
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return {
      message: 'Network connection failed. Please check your internet connection.',
      code: 'NETWORK_ERROR',
      details: null
    };
  }

  if (error.code === 'ECONNABORTED') {
    return {
      message: 'Request timed out. Please try again.',
      code: 'TIMEOUT',
      details: null
    };
  }

  if (error.response) {
    const status = error.response.status;
    const serverMessage = error.response.data?.message || error.response.data?.error;

    if (status === 401) {
      return { message: 'Authentication required. Please log in again.', code: 'AUTH_REQUIRED', details: null };
    }
    if (status === 403) {
      return { message: 'Access denied.', code: 'FORBIDDEN', details: null };
    }
    if (status === 404) {
      return { message: 'Resource not found.', code: 'NOT_FOUND', details: null };
    }
    if (status === 400) {
      return { message: serverMessage || 'Invalid request data.', code: 'VALIDATION_ERROR', details: error.response.data };
    }
    if (status >= 500) {
      return { message: 'Server error. Please try again later.', code: 'SERVER_ERROR', details: null };
    }

    return { message: serverMessage || `Request failed with status ${status}`, code: 'API_ERROR', details: error.response.data };
  }

  return { message: error.message || 'An unexpected error occurred.', code: 'UNKNOWN_ERROR', details: null };
};

// ========================================
// ✅ PRODUCTION: Pending Sync Queue Management
// ========================================
const PENDING_SYNCS_KEY = 'pendingSpeedTests';
const MAX_PENDING_SYNCS = 10;

/**
 * Save a failed sync for later retry
 * @param {Object} syncData - The data that failed to sync
 */
export const savePendingSync = (syncData) => {
  try {
    const existingPending = JSON.parse(localStorage.getItem(PENDING_SYNCS_KEY) || '[]');
    existingPending.push({
      ...syncData,
      retryCount: 0,
      createdAt: new Date().toISOString(),
    });
    // Keep only the last N pending syncs
    const trimmedPending = existingPending.slice(-MAX_PENDING_SYNCS);
    localStorage.setItem(PENDING_SYNCS_KEY, JSON.stringify(trimmedPending));
    console.log('📦 Saved pending sync for later retry:', syncData.venueId);
    return true;
  } catch (error) {
    console.error('⚠️ Could not save pending sync:', error);
    return false;
  }
};

/**
 * Get all pending syncs
 * @returns {Array} - Array of pending sync objects
 */
export const getPendingSyncs = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_SYNCS_KEY) || '[]');
  } catch (error) {
    console.error('⚠️ Could not read pending syncs:', error);
    return [];
  }
};

/**
 * Clear all pending syncs
 */
export const clearPendingSyncs = () => {
  try {
    localStorage.removeItem(PENDING_SYNCS_KEY);
    console.log('🧹 Cleared all pending syncs');
  } catch (error) {
    console.error('⚠️ Could not clear pending syncs:', error);
  }
};

/**
 * Retry all pending syncs
 * @returns {Promise<{success: number, failed: number}>}
 */
export const retryPendingSyncs = async () => {
  const pending = getPendingSyncs();
  if (pending.length === 0) {
    return { success: 0, failed: 0 };
  }

  console.log(`🔄 Retrying ${pending.length} pending syncs...`);

  let success = 0;
  let failed = 0;
  const stillPending = [];

  for (const sync of pending) {
    try {
      const token = getToken();
      if (!token) {
        console.log('⚠️ No token available, skipping pending sync retry');
        stillPending.push(sync);
        failed++;
        continue;
      }

      // Retry the sync
      const response = await api.post('/wifi-speed/test', sync.testData);

      if (response.data?.success) {
        console.log('✅ Pending sync succeeded:', sync.venueId);
        success++;
      } else {
        throw new Error(response.data?.message || 'Sync failed');
      }
    } catch (error) {
      console.error('❌ Pending sync retry failed:', sync.venueId, error.message);

      // Increment retry count
      sync.retryCount = (sync.retryCount || 0) + 1;
      sync.lastRetryAt = new Date().toISOString();
      sync.lastError = error.message;

      // Keep if under 5 retries and less than 24 hours old
      const ageHours = (Date.now() - new Date(sync.createdAt).getTime()) / (1000 * 60 * 60);
      if (sync.retryCount < 5 && ageHours < 24) {
        stillPending.push(sync);
      } else {
        console.log('🗑️ Discarding old/exhausted pending sync:', sync.venueId);
      }
      failed++;
    }
  }

  // Update storage with remaining pending syncs
  localStorage.setItem(PENDING_SYNCS_KEY, JSON.stringify(stillPending));

  console.log(`📊 Pending sync retry complete: ${success} succeeded, ${failed} failed, ${stillPending.length} remaining`);
  return { success, failed, remaining: stillPending.length };
};

// ✅ PRODUCTION: Auto-retry pending syncs on network restore
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('🌐 Network restored, retrying pending syncs...');
    setTimeout(() => {
      retryPendingSyncs().catch(console.error);
    }, 2000); // Wait 2 seconds for connection to stabilize
  });
}

// ==== Admin APIs ====
export const adminAPI = {
  getVenuesForMap: () => api.get("/admin/venues/map"),
  assignVenuesToAgent: (data) => api.post("/admin/venues/assign", data),
  unassignVenue: (tempVenueId) =>
    api.delete(`/admin/venues/${tempVenueId}/assignment`),
  getAgents: () => api.get("/admin/agents"),
  getAssignmentStats: () => api.get("/admin/assignments/stats"),
  deleteVenue: (tempVenueId) => api.delete(`/admin/venues/${tempVenueId}`),
  getAgentVisits: (params) => api.get("/admin/agent-visits", { params }),
  getAgentTrail:  (params) => api.get("/admin/agent-trail",  { params }),
  getAgentUsers:  ()       => api.get("/admin/agent-users"),
  getAgentStats:  (params) => api.get("/admin/agent-stats",  { params }),
  // EV-687 — venue-type assignment split-map screen
  getUntypedVenuesGeo: (region, scope) => api.get('/admin/venues/type-assignment/untyped-geo', { params: { region, scope, limit: scope === 'all' ? 12000 : 5000 } }),
  getVenueTaxonomy:    () => api.get('/admin/venues/type-assignment/taxonomy'),
  assignVenueType:     (venueId, payload) => api.put(`/admin/venues/${venueId}/assign-type`, payload),
  promoteVenueType:    (venueId, type_id) => api.put(`/admin/venues/${venueId}/promote-type`, { type_id }), // EV-687 make secondary primary
  getVenueTypeSuggestions: (venueId) => api.get(`/agent/v2/venues/${venueId}/type-suggestions`), // EV-687 — Google→HL one-click suggestions (same engine as the table)
  // EV-541 — Google→S3 photo backlog map
  getGoogleBacklogVenues: (params = {}) => api.get('/admin/media-manager/google-backlog/venues', { params }),                       // AD-779 — mediaType/datePreset
  getGoogleBacklogPhotos: (venueId, params = {}) => api.get('/admin/media-manager/google-backlog/photos', { params: { venueId, ...params } }), // AD-779
  getVenueContext: (venueId) => api.get('/admin/media-manager/google-backlog/context', { params: { venueId } }), // EV-687 — menu items + menu photos + photos + notes
  getCityGalleryConfig: (region = 'vn') => api.get('/city-gallery/config', { params: { region } }),   // AD-784 — curation panel
  updateCityGalleryConfig: (updates) => api.patch('/city-gallery/config', { updates }),                // AD-784 — toggle which AI types show
  getCityGallerySettings: () => api.get('/city-gallery/settings'),                                     // AD-1347 — card style + blend shares
  // CON-1384 — venue/photo counts at every dB threshold, so the quiet slider shows
  // what it actually selects instead of making you guess what "55 dB" means.
  getQuietCalibration: (minScans = 1) => api.get('/city-gallery/quiet-calibration', { params: { minScans } }),
  updateCityGallerySettings: (updates) => api.patch('/city-gallery/settings', { updates }),            // AD-1347
  // AD-786 — unified Places Manager
  getPlaceTypes: () => api.get('/admin/places/types'),
  updatePlaceType: (key, patch) => api.patch(`/admin/places/types/${key}`, patch),
  getPlaces: (params = {}) => api.get('/admin/places', { params }),
  getPlacePhotos: (type) => api.get('/admin/places/photos', { params: { type } }),
  makePlacesFromPhotos: (type, mediaIds) => api.post('/admin/places/from-photos', { type, media_ids: mediaIds }),
  approveMediaBulk: (mediaIds) => api.patch('/admin/media-manager/media/bulk', { mediaIds, update: { reviewStatus: 'Approved', publicVisibility: 'Public (frontend)' } }),
  rejectMediaBulk: (mediaIds) => api.patch('/admin/media-manager/media/bulk', { mediaIds, update: { reviewStatus: 'Deleted', publicVisibility: 'Hidden' } }),
  // Prospecting
  addVenueAsProspect: (payload) => api.post('/admin/venues/prospect/add', payload, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  getAssignmentAgents: () => api.get('/admin/v2/assignments/agents'),
  // Bulk Pin Align (desktop tool).
  // Either pass a `bbox` string (viewport mode) OR a `q` string (name search).
  // Calling with neither will 400 — we don't ever load all venues to the browser.
  listVenuesForBulkAlign: (params) =>
    api.get('/admin/venues/bulk-align-list', { params: typeof params === 'string' ? { bbox: params } : (params || {}) }),
  bulkUpdateLocations: (updates, source) =>
    api.post('/admin/venues/bulk-update-locations', { updates, source }),
  // GPS change log (audit viewer)
  listGpsChanges: (params) => api.get('/admin/gps-changes', { params }),
  getGpsChangeStats: () => api.get('/admin/gps-changes/stats'),
  getGpsChangeSources: () => api.get('/admin/gps-changes/sources'),
  getGpsChangeUsers: () => api.get('/admin/gps-changes/users'),
  getVenueGpsJourney: (venueId) => api.get(`/admin/gps-changes/venue/${venueId}`),
  // Venue boundary polygons drawn in agent mode (agents.venue_boundaries JSONB table).
  // This is the table populated by the agent boundary tool — has all the recent VN work.
  // The OTHER table (venues.venue_boundaries / PostGIS) only has 37 old Phuket polygons.
  // Endpoint returns { success, boundaries: { type:'FeatureCollection', features } }.
  getAllBoundariesGeoJSON: () => api.get('/agent/v2/boundaries/all'),
};

// ==== Agent APIs ====
export const agentAPI = {
  // AUTH APIs
  sendEmailOTP: (data) =>
    axios.post(`${API_BASE_URL}/auth/send-email-otp`, data),
  verifyEmailOTP: (data) =>
    axios.post(`${API_BASE_URL}/auth/verify-email-otp`, data),
  sendPhoneOTP: (data) => axios.post(`${API_BASE_URL}/auth/send-otp`, data),
  verifyPhoneOTP: (data) => axios.post(`${API_BASE_URL}/auth/verify-otp`, data),
  googleTokenAuth: (data) => axios.post(`${API_BASE_URL}/auth/google/token`, data),

  // ✅ GET VENUE BY TEMP ID
  getVenueByTempId: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}`),

  // VENUE OPS
  quickAddVenue: (data) => api.post("/agent/venues/quick-add", data),
  getAgentVenues: (params) => api.get("/agent/venues", { params }),
  getRegionalVenues: () => api.get("/agent/venues/regional"),

  // ✅ Onboard from Google with comprehensive debugging
  onboardFromGoogle: (placeData) => {
    console.log("📤 API: Sending Google place data to backend:", placeData.name);
    console.log("📦 API: Full request body structure:", {
      googlePlaceId: placeData.googlePlaceId,
      name: placeData.name,
      latitude: placeData.latitude,
      longitude: placeData.longitude,
      allKeys: Object.keys(placeData),
      dataType: typeof placeData,
      isObject: placeData instanceof Object,
      stringified: JSON.stringify(placeData, null, 2).substring(0, 500) + '...'
    });

    // ✅ Validate data before sending
    if (!placeData.googlePlaceId || !placeData.name) {
      console.error('❌ API: Missing required fields before sending!', {
        googlePlaceId: placeData.googlePlaceId,
        name: placeData.name
      });
    }

    // ✅ Send placeData directly (not nested)
    return api.post("/agent/venues/onboard-from-google", placeData)
      .then((response) => {
        console.log("📥 API: Received response from backend:", response.data);

        // ✅ Log auto-linking status
        if (response.data.autoLinked && response.data.autoCreated) {
          console.log("✅ API: Venue auto-linked successfully");
          console.log("  - Temp ID:", response.data.data.tempVenueId);
          console.log("  - CRM ID:", response.data.data.crmId);
          console.log("  - Venue ID:", response.data.data.venueId);
        } else if (response.data.autoLinked === false) {
          console.warn("⚠️ API: Auto-link failed:", response.data.linkError);
        }

        return response;
      })
      .catch((error) => {
        console.error("❌ API: Request failed:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          message: error.message
        });
        throw error;
      });
  },

  updateVenueStatus: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/status`, data),

  // ✅ Keep for manual linking fallback
  linkToCRM: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/link-crm`, data),

  finalizeOnboarding: (tempVenueId) =>
    api.post(`/agent/venues/${tempVenueId}/finalize`),

  // ZONE OPS
  createZone: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/zones`, data),

  // ✅ NEW: Update zone
  updateZone: (venueId, zoneId, data) =>
    api.put(`/agent/venues/${venueId}/zones/${zoneId}`, data),

  getZones: (venueId) => api.get(`/agent/venues/${venueId}/zones`),

  deleteZone: (venueId, zoneId) =>
    api.delete(`/agent/venues/${venueId}/zones/${zoneId}`),

  // ✅ ZONE PHOTO UPLOAD
  uploadZonePhoto: (formData, onUploadProgress) =>
    retryRequest(
      () => {
        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post('/agent/zones/upload-photo', formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),

  // QR OPS
  attachMainQR: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/qr/main`, data),
  getMainQR: (venueId) => api.get(`/agent/venues/${venueId}/qr/main`),
  linkTableQR: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/qr/table`, data),
  getTableQRs: (venueId) => api.get(`/agent/venues/${venueId}/qr/table`),
  deleteTableQR: (venueId, bindingId) =>
    api.delete(`/agent/venues/${venueId}/qr/table/${bindingId}`),
  generateTestToken: (bindingId) =>
    api.post(`/agent/qr/${bindingId}/test-token`),

  // PHOTO OPS (Legacy)
  uploadPhotos: (venueId, formData) =>
    api.post(`/agent/venues/${venueId}/photos`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  getPhotos: (venueId, type) =>
    api.get(`/agent/venues/${venueId}/photos`, { params: { type } }),
  deletePhoto: (venueId, assetId) =>
    api.delete(`/agent/venues/${venueId}/photos/${assetId}`),

  // ✅ MEDIA OPS (New - S3 Direct Upload with Retry Logic)
  uploadVenueMedia: (tempVenueId, formData, onUploadProgress) =>
    retryRequest(
      () => {
        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post(`/agent/venues/${tempVenueId}/media`, formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),

  getVenueMedia: (tempVenueId) => api.get(`/agent/venues/${tempVenueId}/media`),

  getMediaStats: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/media/stats`),

  deleteVenueMedia: (tempVenueId, mediaId) =>
    api.delete(`/agent/venues/${tempVenueId}/media/${mediaId}`),

  // WIFI OPS (Legacy)
  runWiFiTest: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/wifi-test`, data),
  getWiFiTests: (venueId) => api.get(`/agent/venues/${venueId}/wifi-tests`),

  // VISIT OPS
  logVisit: (data) => api.post("/agent/visits", data),
  getVisits: (venueId) => api.get(`/agent/visits/${venueId}`),

  // GPS OPS
  updateVenueGPS: (tempVenueId, gpsData) =>
    api.put(`/agent/venues/${tempVenueId}/gps`, gpsData),

  // ✅ GEOFENCING APIs
  updateVenueGeofence: (tempVenueId, geofenceData) =>
    api.put(`/agent/venues/${tempVenueId}/geofence`, geofenceData),

  getVenueGeofence: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/geofence`),

  deleteVenueGeofence: (tempVenueId) =>
    api.delete(`/agent/venues/${tempVenueId}/geofence`),

  // ✅ PAYMENT TYPES APIs (Legacy)
  updatePaymentTypes: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/payment-types`, data),

  getPaymentTypes: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/payment-types`),

  // ✅ PAYMENT METHODS APIs (New)
  updatePaymentMethods: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/payment-methods`, data),

  getPaymentMethods: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/payment-methods`),

  // ✅ CARD MACHINE APIs
  addCardMachine: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/card-machines`, data),

  getCardMachines: (venueId) =>
    api.get(`/agent/venues/${venueId}/card-machines`),

  deleteCardMachine: (venueId, machineId) =>
    api.delete(`/agent/venues/${venueId}/card-machines/${machineId}`),

  // ✅ UPI/QR PAYMENT APIs
  addUpiQrPayment: (venueId, data) =>
    api.post(`/agent/venues/${venueId}/upi-qr-payments`, data),

  getUpiQrPayments: (venueId) =>
    api.get(`/agent/venues/${venueId}/upi-qr-payments`),

  deleteUpiQrPayment: (venueId, qrId) =>
    api.delete(`/agent/venues/${venueId}/upi-qr-payments/${qrId}`),

  // ✅ QR CODE PARSER
  parseQrCode: (data) =>
    api.post('/agent/parse-qr-code', data),

  updateVenueInfo: (tempVenueId, venueData) =>
    api.put(`/agent/venues/${tempVenueId}/info`, venueData),

  // ✅ CATEGORY & TYPE APIs
  updateVenueCategoryType: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/category-type`, data),

  getVenueCategoryType: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/category-type`),

  // ✅ VITALS OPERATIONS
  getVenueVitals: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/vitals`),

  createVenueVitals: (tempVenueId, vitalsData) =>
    api.post(`/agent/venues/${tempVenueId}/vitals`, vitalsData),

  updateVenueVitals: (tempVenueId, vitalsData) =>
    api.put(`/agent/venues/${tempVenueId}/vitals`, vitalsData),

  // ASSIGNMENTS
  getMyAssignments: (params) => api.get("/agent/my-assignments", { params }),
  getFieldAssignments: () => api.get("/agent/v2/field-assignments"),
  getFieldAllVenues: (bounds) => {
    if (bounds) {
      const { minLat, maxLat, minLng, maxLng } = bounds;
      return api.get(`/agent/v2/field-all-venues?minLat=${minLat}&maxLat=${maxLat}&minLng=${minLng}&maxLng=${maxLng}`);
    }
    return api.get("/agent/v2/field-all-venues");
  },
  getWMVenueIds: () => api.get("/wifi/wm-venue-slugs"),
  uploadVenuePhotoV2: (venueId, formData, onUploadProgress) =>
    api.post(`/agent/v2/venues/${venueId}/media`, formData, {
      // Do NOT manually set Content-Type — axios auto-sets multipart/form-data with correct boundary
      onUploadProgress,
      timeout: 300000,
    }),
  // ZONE MANAGEMENT (v2 — 100% Neon)
  getVenueZones:    (venueId)                  => api.get(`/agent/v2/venues/${venueId}/zones`),
  createVenueZone:  (venueId, data)            => api.post(`/agent/v2/venues/${venueId}/zones`, data),
  updateVenueZone:  (venueId, zoneId, data)    => api.put(`/agent/v2/venues/${venueId}/zones/${zoneId}`, data),
  // STATUS, NOTES, GPS, TAXONOMY, CATEGORY (v2 — 100% Neon)
  getVenueStatus:   (venueId)       => api.get(`/agent/v2/venues/${venueId}/status`),
  getVenueNotesV2:  (venueId)       => api.get(`/agent/v2/venues/${venueId}/notes`),
  createVenueNoteV2:(venueId, data) => api.post(`/agent/v2/venues/${venueId}/notes`, data),
  verifyVenueGPS:    (venueId, data) => api.post(`/agent/v2/venues/${venueId}/gps-verify`, data),
  getVenueGPSHistory:(venueId)      => api.get(`/agent/v2/venues/${venueId}/gps-history`),
  getVenueBoundary:  (venueId)      => api.get(`/agent/v2/venues/${venueId}/boundary`),
  saveVenueBoundary: (venueId, pts) => api.put(`/agent/v2/venues/${venueId}/boundary`, { boundary_points: pts }),
  getVenuePhotosV2:    (venueId)           => api.get(`/agent/v2/venues/${venueId}/photos`),
  setPhotoMediaType:   (mediaId, type)     => api.patch(`/agent/v2/media/${mediaId}/type`, { media_type: type }),
  addPhotoTag:         (mediaId, tag)      => api.post(`/agent/v2/media/${mediaId}/tags`, { tag }),
  removePhotoTag:      (mediaId, tag)      => api.delete(`/agent/v2/media/${mediaId}/tags/${encodeURIComponent(tag)}`),
  getActiveVisit:      ()                  => api.get('/agent/v2/visits/active'),
  postTrailBatch:      (points)            => api.post('/agent/v2/trail/batch', { points }),
  venueCheckin:        (venueId, data)     => api.post(`/agent/v2/venues/${venueId}/checkin`, data || {}),
  venueCheckout:       (visitId, data)     => api.patch(`/agent/v2/visits/${visitId}/checkout`, data || {}),
  getVenueVisits:      (venueId)           => api.get(`/agent/v2/venues/${venueId}/visits`),
  getVenueTaxonomy:    ()                  => api.get('/agent/v2/taxonomy'),
  // CREATE VENUE (field agent — no Google Places)
  createFieldVenue: (data) => api.post('/agent/v2/venues/create', data),
  getVenueCategories:  (venueId)           => api.get(`/agent/v2/venues/${venueId}/categories`),
  addVenueCategory:    (venueId, data)     => api.post(`/agent/v2/venues/${venueId}/categories`, data),
  deleteVenueCategory: (venueId, catRowId) => api.delete(`/agent/v2/venues/${venueId}/categories/${catRowId}`),
  setVenueCategory: (venueId, data) => api.put(`/agent/v2/venues/${venueId}/category`, data),
  saveWifiTest:      (venueId, data) => api.post(`/agent/v2/venues/${venueId}/wifi-test`, data),
  getVenueWifiTests: (venueId)       => api.get(`/agent/v2/venues/${venueId}/wifi-tests`),
  getVenueNoiseMeasurements: (venueId) => api.get(`/noise/venue/${venueId}`),
  getVenueNoiseProfile:      (venueId) => api.get(`/noise/venue/${venueId}/profile`),
  submitNoiseScan:           (venueId, data) => api.post(`/noise/venue/${venueId}/scan`, data),
  // WiFi Networks (SSID layer)
  getWifiMethodTypes:     ()                    => api.get('/agent/v2/wifi/method-types'),
  getWifiLocationTypes:   ()                    => api.get('/agent/v2/wifi/location-types'),
  getVenueWifiNetworks:   (venueId)             => api.get(`/agent/v2/venues/${venueId}/wifi-networks`),
  saveVenueWifiNetwork:   (venueId, data)       => api.post(`/agent/v2/venues/${venueId}/wifi-networks`, data),
  updateVenueWifiNetwork: (id, data)            => api.patch(`/agent/v2/wifi-networks/${id}`, data),
  getVenueWifiDisplays:   (venueId)             => api.get(`/agent/v2/venues/${venueId}/wifi-displays`),
  saveVenueWifiDisplay:   (venueId, data)       => api.post(`/agent/v2/venues/${venueId}/wifi-displays`, data),
  deleteVenueWifiDisplay: (id)                  => api.delete(`/agent/v2/wifi-displays/${id}`),
  extractWifiFromPhoto:   (data)                => api.post('/agent/v2/wifi/extract-from-photo', data),
  markVenueVisited: (tempVenueId, data) =>
    api.put(`/agent/venues/${tempVenueId}/visit`, data),
  softOnboardVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/soft-onboard`, data),
  declineVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/decline`, data),
  captureLeadVenue: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/capture-lead`, data),
  getMyStats: () => api.get("/agent/my-stats"),

  // ✅ NOTES OPERATIONS
  addVenueNote: (tempVenueId, data) =>
    api.post(`/agent/venues/${tempVenueId}/notes`, data),

  getVenueNotes: (tempVenueId, noteType = null) =>
    api.get(`/agent/venues/${tempVenueId}/notes`, {
      params: noteType ? { noteType } : {},
    }),

  updateVenueNote: (tempVenueId, noteId, data) =>
    api.put(`/agent/venues/${tempVenueId}/notes/${noteId}`, data),

  deleteVenueNote: (tempVenueId, noteId) =>
    api.delete(`/agent/venues/${tempVenueId}/notes/${noteId}`),

  // ✅ EVENTS OPERATIONS
  getVenueEvents: (tempVenueId) =>
    api.get(`/agent/venues/${tempVenueId}/events`),

  addVenueEvent: (tempVenueId, eventData) =>
    api.post(`/agent/venues/${tempVenueId}/events`, eventData),

  updateVenueEvent: (tempVenueId, eventId, eventData) =>
    api.put(`/agent/venues/${tempVenueId}/events/${eventId}`, eventData),

  deleteVenueEvent: (tempVenueId, eventId) =>
    api.delete(`/agent/venues/${tempVenueId}/events/${eventId}`),

  // ✅ NEW: EVENT PHOTO UPLOAD (Same pattern as Zone Photo)
  uploadEventPhoto: (photoFile, onUploadProgress) =>
    retryRequest(
      () => {
        const formData = new FormData();
        formData.append('eventPhoto', photoFile);

        const config = {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutes for large files
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        };

        if (typeof onUploadProgress === 'function') {
          config.onUploadProgress = onUploadProgress;
        }

        return api.post('/agent/events/upload-photo', formData, config);
      },
      2, // 2 retries
      3000 // 3 second initial delay
    ),

  // ===== BULK PHOTO UPLOAD =====
  bulkUpload: {
    // Initiate a new bulk upload batch
    initiate: (expectedPhotoCount) =>
      api.post('/admin/bulk-upload/initiate', { expectedPhotoCount }),

    // Process photos in a batch
    process: (batchId, files, onProgress, hammingThreshold = 10) => {
      const formData = new FormData();
      formData.append('batchId', batchId);
      formData.append('hammingThreshold', String(hammingThreshold));
      files.forEach(file => formData.append('files', file));

      return api.post('/admin/bulk-upload/process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress,
        timeout: 300000, // 5 minutes
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
    },

    // Get batch status
    getBatch: (batchId) =>
      api.get(`/admin/bulk-upload/batch/${batchId}`),

    // Finalize assignments (smart 3-bucket: assign + backlog + discard)
    finalize: (batchId, assignments, discardIds = [], backlogIds = []) =>
      api.post(`/admin/bulk-upload/batch/${batchId}/assign`, { assignments, discardIds, backlogIds }),

    // Replace old lower-quality duplicate with new higher-quality upload
    replaceUpgrade: (batchId, photoId) =>
      api.post(`/admin/bulk-upload/batch/${batchId}/photos/${photoId}/replace-upgrade`),

    // Cancel batch and clean up pending VenueMedia records
    cancel: (batchId) =>
      api.delete(`/admin/bulk-upload/batch/${batchId}`),

    // Persist group pool assignments + skipped photos (debounced save)
    updatePools: (batchId, groupPools, photoSkipped) =>
      api.patch(`/admin/bulk-upload/batch/${batchId}/pools`, { groupPools, photoSkipped }),

    // List in-progress batches for current user (for idle screen resume panel)
    getMyBatches: () =>
      api.get('/admin/bulk-upload/my-batches'),

    // Recent venues with media submissions (history panel)
    getRecentVenues: (limit = 100) =>
      api.get(`/admin/bulk-upload/recent-venues`, { params: { limit } }),

    // Count of Approved VenueMedia per mediaType (for popularity-sorted picker)
    getMediaTypeCounts: () =>
      api.get('/admin/bulk-upload/media-type-counts'),

    // Venues from honestlee_th within a lat/lng bounding box (for map markers)
    getVenuesInBounds: ({ north, south, east, west, limit = 100 }) =>
      api.get('/admin/bulk-upload/venues-in-bounds', { params: { north, south, east, west, limit } }),

    // Persist per-photo mediaType selections to batch (debounced auto-save from review UI)
    savePhotoTypes: (batchId, photoTypes) =>
      api.patch(`/admin/bulk-upload/batch/${batchId}/photo-types`, { photoTypes }),

    // Local ingest — creates batch from local_hash_scan, no S3 upload yet
    ingestLocal: (scanIds, venueId = null, venueName = null) =>
      api.post('/admin/bulk-upload/ingest-local', { scanIds, venueId, venueName }),

    // Promote local photos to S3 (fire-and-forget) when moved to GPS Assigned pool
    // venueAssignments: { [localScanId]: venueId } — the venue selected in the uploader
    // right now. Backend prefers it over the ingest-time guess stored in the batch (v69.901).
    promoteLocal: (batchId, scanIds, venueAssignments = null) =>
      api.post(`/admin/bulk-upload/batch/${batchId}/promote-local`, { scanIds, venueAssignments }),

    // Put local photos BACK on the Local Photos Map — undoes the S3 promote, clears
    // matched_media_id and restores the file on disk, then drops them from the batch (v69.902)
    releaseLocal: (batchId, scanIds) =>
      api.post(`/admin/bulk-upload/batch/${batchId}/release-local`, { scanIds }),

    // Local finalize — assigns local files directly to a venue (no batch review)
    localFinalize: (scanIds, venueId, mediaType, hammingThreshold = 10) =>
      api.post('/admin/bulk-upload/local-finalize', { scanIds, venueId, mediaType, hammingThreshold }),
  },

  // Local Photos Map — reads media.local_hash_scan
  localPhotos: {
    getVenueGroups: (radiusM, opts = {}) => {
      const params = {};
      if (radiusM) params.radiusM = radiusM;
      if (opts.months && opts.months.length > 0) params.months = opts.months.join(',');
      return api.get('/admin/local-photos/venue-groups', { params });
    },
    getGpsPoints: (params = {}) =>
      api.get('/admin/local-photos/gps-points', { params }),
    getStats: () =>
      api.get('/admin/local-photos/stats'),
    getMonthStats: (bounds = null) =>
      api.get('/admin/local-photos/month-stats', { params: bounds || {} }),
    getVenueStats: (venueId) =>
      api.get(`/admin/local-photos/venue-stats/${venueId}`),
    getNearestVenues: (lat, lng, limit = 5) =>
      api.get('/admin/local-photos/nearest-venues', { params: { lat, lng, limit } }),
    getPhotosInBounds: (bounds, opts = {}) =>
      api.get('/admin/local-photos/photos', { params: { ...bounds, ...opts } }),
    getNearestVenue: (lat, lng) =>
      api.get('/admin/local-photos/nearest-venue', { params: { lat, lng } }),
    getByIds: (ids) =>
      api.get('/admin/local-photos/by-ids', { params: { ids: ids.join(',') } }),
    search: (q) =>
      api.get('/admin/local-photos/search', { params: { q } }),
    // Use with authenticated fetch() — standard admin auth header required
    thumbnailPath: (id) => `/api/admin/local-photos/thumbnail/${id}`,
  },

  // Search venues (for manual selection)
  searchVenues: (query, opts = {}) =>
    api.get('/venues/search', { params: { q: query, limit: opts.limit || 10, ...(opts.region && { region: opts.region }) } })
};

// ==== ✅ WiFi Speed Test APIs - PRODUCTION READY ====
export const wifiSpeedAPI = {
  // ✅ PRODUCTION: Authenticated endpoint with retry logic and pending sync storage
  submitSpeedTest: async (data) => {
    console.log('📊 Submitting speed test:', {
      venueId: data.venueId,
      tempVenueId: data.tempVenueId,
      ssid: data.ssid,
      downloadMbps: data.downloadMbps,
      uploadMbps: data.uploadMbps
    });

    // Check for auth token before making request
    const token = getToken();
    if (!token) {
      console.error('❌ No authentication token available');

      // ✅ PRODUCTION: Save for later retry when user logs in
      savePendingSync({
        venueId: data.venueId || data.tempVenueId,
        testData: data,
        error: 'AUTH_REQUIRED'
      });

      const error = new Error('Authentication required to save speed test');
      error.errorCode = 'AUTH_REQUIRED';
      error.userMessage = 'Please log in to save your speed test results. Data saved locally.';
      throw error;
    }

    try {
      // Use retry logic for transient failures
      const response = await retryRequest(
        () => api.post("/wifi-speed/test", data),
        2, // 2 retries
        2000 // 2 second initial delay
      );

      // ✅ PRODUCTION: Check for partial success (speed test saved but credentials sync failed)
      if (response.data?.success) {
        if (response.data.credentialsSynced === false) {
          console.warn('⚠️ Speed test saved but WiFi credentials sync failed:', response.data.syncError);

          // Attach warning info to response for UI display
          response.data.warning = true;
          response.data.warningMessage = 'Speed test saved but WiFi credentials could not be synced. They will be saved on your next test.';
        } else {
          console.log('✅ Speed test and credentials synced successfully');
        }
      }

      console.log('✅ Speed test submitted successfully:', {
        success: response.data?.success,
        credentialsSynced: response.data?.credentialsSynced,
        testId: response.data?.data?.testId
      });

      return response;
    } catch (error) {
      // Extract detailed error information
      const errorDetails = extractErrorDetails(error);
      console.error('❌ Failed to submit speed test:', {
        code: errorDetails.code,
        message: errorDetails.message,
        originalError: error.message,
        status: error.response?.status,
        serverMessage: error.response?.data?.message
      });

      // ✅ PRODUCTION: Save pending sync for network/server errors
      if (errorDetails.code === 'NETWORK_ERROR' ||
        errorDetails.code === 'TIMEOUT' ||
        errorDetails.code === 'SERVER_ERROR') {
        savePendingSync({
          venueId: data.venueId || data.tempVenueId,
          testData: data,
          error: errorDetails.code
        });
        console.log('📦 Speed test data saved locally for retry');
      }

      // Attach user-friendly message to error for UI display
      error.userMessage = errorDetails.message;
      error.errorCode = errorDetails.code;
      throw error;
    }
  },

  // ✅ NEW: Save WiFi credentials only (without running speed test)
  saveWiFiCredentialsOnly: async (data) => {
    console.log('💾 Saving WiFi credentials only (no speed test):', {
      venueId: data.venueId,
      tempVenueId: data.tempVenueId,
      ssid: data.ssid,
      hasPassword: !!data.wifiPassword,
      isWifiFree: data.isWifiFree
    });

    // Check for auth token before making request
    const token = getToken();
    if (!token) {
      console.error('❌ No authentication token available');
      const error = new Error('Authentication required to save WiFi credentials');
      error.errorCode = 'AUTH_REQUIRED';
      error.userMessage = 'Please log in to save WiFi credentials.';
      throw error;
    }

    // Prepare payload with zero speed values
    const credentialsPayload = {
      venueId: data.venueId,
      tempVenueId: data.tempVenueId,
      ssid: data.ssid,
      userProvidedSsid: data.ssid,
      wifiPassword: data.wifiPassword || undefined,
      isWifiFree: data.isWifiFree,
      wifiPasswordNote: data.wifiPasswordNote || undefined,
      isOnVenueWifi: data.isOnVenueWifi !== undefined ? data.isOnVenueWifi : true,
      // Zero speed values for credentials-only submission
      downloadMbps: 0,
      uploadMbps: 0,
      latencyMs: 0,
      testMethod: 'credentials_only',
      deviceInfo: data.deviceInfo || { model: 'Unknown', os: 'Unknown', browser: 'Unknown' },
      // Optional contextual fields
      zoneId: data.zoneId,
      zoneName: data.zoneName,
      displayMethod: data.displayMethod || 'credentials_only',
      displayLocation: data.displayLocation,
      hasNoWifi: data.hasNoWifi || false,
    };

    try {
      // Use retry logic for transient failures
      const response = await retryRequest(
        () => api.post("/wifi-speed/test", credentialsPayload),
        2, // 2 retries
        2000 // 2 second initial delay
      );

      if (response.data?.success) {
        console.log('✅ WiFi credentials saved successfully:', {
          credentialsSynced: response.data.credentialsSynced,
          testId: response.data?.data?.testId
        });
      }

      return response;
    } catch (error) {
      const errorDetails = extractErrorDetails(error);
      console.error('❌ Failed to save WiFi credentials:', {
        code: errorDetails.code,
        message: errorDetails.message,
        originalError: error.message
      });

      error.userMessage = errorDetails.message;
      error.errorCode = errorDetails.code;
      throw error;
    }
  },

  // ✅ NEW: Get available SSIDs for a venue (shows SSIDs from all sources including consumer app)
  getVenueSSIDs: (venueId) => {
    console.log('📶 Fetching SSIDs for venue:', venueId);
    return axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}/ssids`, {
      headers: {
        'Content-Type': 'application/json',
        'x-region': getRegion()
      }
    })
      .then(response => {
        console.log('✅ SSIDs loaded:', response.data?.data?.ssids?.length || 0);
        return response;
      })
      .catch(error => {
        console.error('❌ Failed to fetch SSIDs:', error.response?.data || error.message);
        throw error;
      });
  },

  getVenueSpeedTests: (venueId, params = {}) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}`, { params }),
  getMySpeedTests: (params = {}) => api.get("/wifi-speed/my-tests", { params }),
  deleteSpeedTest: (testId) => api.delete(`/wifi-speed/test/${testId}`),
  getVenueWiFiStats: (venueId) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}/stats`),
  getVenueHeatmap: (venueId, type = 'all') =>
    api.get(`/wifi-speed/venue/${venueId}/heatmap`, { params: { type } }),

  // Standalone speed test (no venue — for cellular coverage heatmap)
  submitStandaloneSpeedTest: (data) => api.post('/wifi-speed/standalone', data),
};

// ==== AI Classification Schema APIs ====
export const schemaAPI = {
  getAll: (status) => api.get('/admin/classification/schema', { params: status ? { status } : {} }),
  create: (data) => api.post('/admin/classification/schema', data),
  update: (enumValue, data) => api.patch(`/admin/classification/schema/${enumValue}`, data),
  deleteType: (enumValue, replacedBy, notes) => api.post(`/admin/classification/schema/${enumValue}/delete`, { replacedBy, notes }),
  mergeType: (enumValue, mergedInto, notes) => api.post(`/admin/classification/schema/${enumValue}/merge`, { mergedInto, notes }),
  restore: (enumValue) => api.post(`/admin/classification/schema/${enumValue}/restore`),
  autocomplete: (q) => api.get('/admin/classification/schema/autocomplete', { params: { q } }),
};

// ==== AI Classification Management APIs ====
export const classificationAPI = {
  // CON-1643 — selected photos -> draft offers. commit:false previews only.
  extractOffers: (mediaIds, commit) => api.post('/admin/media/extract-offers', { media_ids: mediaIds, commit: !!commit }),
  getRules: (status) => api.get('/admin/classification/rules', { params: status ? { status } : {} }),
  createRule: (data) => api.post('/admin/classification/rules', data),
  updateRule: (id, data) => api.patch(`/admin/classification/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/admin/classification/rules/${id}`),
  // v66.128: atomic rule → schema migration with bidirectional linkage
  migrateRuleToSchema: (id, body) => api.post(`/admin/classification/rules/${id}/migrate-to-schema`, body),
  getPromptPreview: () => api.get('/admin/classification/prompt-preview'),
  testClassification: (imageUrl, model) => api.post('/admin/classification/test', { imageUrl, model }),
  getRecentResults: (limit = 200, offset = 0) => api.get('/admin/classification/recent-results', { params: { limit, offset } }),
  rerunClassification: (mediaIds, runName, jobId) => api.post('/admin/classification/rerun', { mediaIds, runName, jobId }, { timeout: 300000 }),
  getRerunProgress: (jobId) => api.get(`/admin/classification/rerun/progress/${jobId}`),
  applyRerunResults: (mediaIds) => api.post('/admin/classification/rerun/apply', { mediaIds }, { timeout: 120000 }),
  getUsageCounts: () => api.get('/admin/classification/schema/usage-counts'),
  schemaAutocomplete: (q) => api.get('/admin/classification/schema/autocomplete', { params: { q } }),
  // v66.111: prompt config CRUD
  listConfigs:        () => api.get('/admin/classification/prompt-configs'),
  getActiveConfig:    () => api.get('/admin/classification/prompt-configs/active'),
  updateConfig:       (configKey, body) => api.patch(`/admin/classification/prompt-configs/${encodeURIComponent(configKey)}`, body),
  promoteConfig:      (configKey) => api.post(`/admin/classification/prompt-configs/${encodeURIComponent(configKey)}/promote`),
  // v66.112: eval harness
  runEval:        (limit) => api.post('/admin/classification/eval/run', { limit }),
  listEvals:      () => api.get('/admin/classification/eval/runs'),
  getEval:        (id) => api.get(`/admin/classification/eval/runs/${id}`),
  // v66.116: engagement bucket counts for filter badges + Eval-tab stats panel
  getEngagementStats: () => api.get('/admin/classification/eval/engagement-stats'),
  getRuns: (limit = 50, offset = 0, source) =>
    api.get('/admin/classification/runs', { params: { limit, offset, ...(source ? { source } : {}) } }),
  getRunDetail: (id) => api.get(`/admin/classification/runs/${id}`),
  // v69.912: prompt version history + diff
  getPromptVersions: () => api.get('/admin/classification/prompt-versions'),
  getPromptVersion: (id) => api.get(`/admin/classification/prompt-versions/${id}`),
  // `from` optional — omit to diff against the immediately preceding version
  getPromptVersionDiff: (to, from) =>
    api.get('/admin/classification/prompt-versions/diff', { params: { to, ...(from ? { from } : {}) } }),
};

// ==== Media Manager APIs ====
export const mediaManagerAPI = {
  list: (params) => api.get('/admin/media-manager/media', { params }),
  // v69.820: ids-only mode — returns { ids: [...], total } for the CURRENT filter
  // (no limit/offset) so the grid can "select all N matching", not just loaded rows.
  listIds: (filters = {}) =>
    api.get('/admin/media-manager/media', { params: { ...filters, idsOnly: true } }),
  bulkUpdate: (mediaIds, update) =>
    api.patch('/admin/media-manager/media/bulk', { mediaIds, update }),
  bulkAssignTag: (mediaIds, tag, clearExisting = false) =>
    api.post('/admin/media-manager/media/bulk-tag', { mediaIds, tag, clearExisting }),
  bulkRemoveTag: (mediaIds, tags) =>
    api.post('/admin/media-manager/media/bulk-remove-tag', { mediaIds, tags }),
  bulkDelete: (mediaIds, opts = {}) =>
    api.post('/admin/media-manager/media/bulk-delete', {
      mediaIds,
      type: opts.type || 'soft',  // v66.85: 'soft' (default) | 'hard' (also remove S3)
      reason: opts.reason || null,
    }),
  // v66.91: per-venue preview overlay data (click venue name → popover)
  venueDetail: (venueId) =>
    api.get('/admin/media-manager/venue-detail', { params: { venueId } }),
  // v68.326: B-15 S2 — events for a venue (event picker in the bulk bar)
  // v70.455: onAt = the photo's FULL capture timestamp so the backend can order by
  // how close each event was to the moment the shutter fired, and drop old/future ones.
  // v70.456: WhatsApp place-mention triage — resolved FREE, approved by hand.
  placeTriage:        (params) => api.get('/admin/place-triage', { params }),
  placeTriageDecide:  (ids, status) => api.post('/admin/place-triage/decide', { ids, status }),
  placeTriageRematch: () => api.post('/admin/place-triage/rematch'),
  venueEvents: (venueId, onDate, onAt, windowHours) =>
    api.get('/admin/media-manager/venue-events', { params: { venueId, onDate, onAt, windowHours } }),
  // v68.326: B-15 S2 — bulk-tag selected media with an event (whole_series by
  // default) or clear the link. payload = { event_id, event_scope, occurrence_date }
  assignEvent: (mediaIds, payload = {}) =>
    api.post('/admin/media-manager/media/assign-event', { mediaIds, ...payload }),
  // v68.365: mobile Quick-Approve — queue of NEW Google-Drive-ingested media
  // (GPS-assigned, unconfirmed) + one-tap bulk approve.
  quickQueue: (params = {}) =>
    api.get('/admin/media-manager/quick-queue', { params }),
  approveVenue: (mediaIds) =>
    api.post('/admin/media-manager/media/approve-venue', { mediaIds }),
  // AD-52: reverse the last Quick Approve swipe (restores prior snapshot)
  quickApproveUndo: (mediaId, kind, prev) =>
    api.post('/admin/media-manager/quick-approve/undo', { mediaId, kind, prev }),
  // v68.385: Tinder venue-picker — matched venue + nearest alternatives by GPS
  nearbyVenues: (mediaId) =>
    api.get(`/admin/media-manager/media/${mediaId}/nearby-venues`),
  // v68.357: amenity counterpart — list a venue's amenity polygons + bulk-tag
  // selected media with an amenity (pool / volleyball court / …) or clear.
  venueAmenities: (venueId) =>
    api.get('/admin/media-manager/venue-amenities', { params: { venueId } }),
  assignAmenity: (mediaIds, amenity_polygon_id = null) =>
    api.post('/admin/media-manager/media/assign-amenity', { mediaIds, amenity_polygon_id }),
  // v68.327: B-15 S2.5 — canonical event media upload (photo OR video). Reuses
  // the dedup + S3 pipeline; on duplicate it links the existing media to the
  // event. Returns { success, mediaId, url, isVideo, linkedExisting? }.
  uploadEventMedia: (file, { eventId, eventScope = 'whole_series', occurrenceDate = null, mediaType } = {}, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('eventId', eventId);
    fd.append('eventScope', eventScope);
    if (occurrenceDate) fd.append('occurrenceDate', occurrenceDate);
    if (mediaType) fd.append('mediaType', mediaType);
    return api.post('/admin/media-manager/upload-event-media', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
      onUploadProgress: onProgress,
    });
  },
  // Returns all distinct filter values from DB (AI types, old types, camera models)
  facets: () => api.get('/admin/media-manager/facets'),
  // Phase 3: per-venue summary (one row per venue with photo counts, types, thumbs)
  venuesSummary: (params) => api.get('/admin/media-manager/venues-summary', { params }),
  // v66.97: per-photo training write — keeps multi-tag, writes human_feedback,
  // optionally creates a pending classification_rules row when ruleText supplied
  correctAi: (mediaId, payload) =>
    api.post(`/admin/media-manager/media/${encodeURIComponent(mediaId)}/correct-ai`, payload),
  // v66.97: trigger single-photo AI classification on demand (~$0.0002 per click)
  runAi: (mediaId) =>
    api.post(`/admin/media-manager/media/${encodeURIComponent(mediaId)}/run-ai`),
  // v66.104: full per-photo AI run history
  runHistory: (mediaId) =>
    api.get(`/admin/media-manager/media/${encodeURIComponent(mediaId)}/runs`),
  // v66.124: promote a historical/eval run as the current canonical state
  promoteRun: (runId) =>
    api.post(`/admin/media-manager/runs/${encodeURIComponent(runId)}/promote`),
};

// ==== Media Reclaim APIs (review + bulk hard-delete of reclaim candidates + S3 orphans) ====
export const mediaReclaimAPI = {
  summary: () => api.get('/admin/media-reclaim/summary'),
  orphans: (params) => api.get('/admin/media-reclaim/orphans', { params }),
  hardDeleteOrphans: (keys) =>
    api.post('/admin/media-reclaim/orphans/hard-delete', { keys }),
};

// ==== Venue Schema Manager APIs ====
export const venueSchemaAPI = {
  getTree: () => api.get('/admin/venue-schema/tree'),
  seed: () => api.post('/admin/venue-schema/seed'),
  getGroups: () => api.get('/admin/venue-schema/groups'),
  createGroup: (data) => api.post('/admin/venue-schema/groups', data),
  updateGroup: (id, data) => api.patch(`/admin/venue-schema/groups/${encodeURIComponent(id)}`, data),
  deleteGroup: (id) => api.delete(`/admin/venue-schema/groups/${encodeURIComponent(id)}`),
  getCategories: (groupId) => api.get('/admin/venue-schema/categories', { params: groupId ? { groupId } : {} }),
  createCategory: (data) => api.post('/admin/venue-schema/categories', data),
  updateCategory: (id, data) => api.patch(`/admin/venue-schema/categories/${encodeURIComponent(id)}`, data),
  deleteCategory: (id) => api.delete(`/admin/venue-schema/categories/${encodeURIComponent(id)}`),
  getTypes: (categoryId) => api.get('/admin/venue-schema/types', { params: categoryId ? { categoryId } : {} }),
  createType: (data) => api.post('/admin/venue-schema/types', data),
  updateType: (id, data) => api.patch(`/admin/venue-schema/types/${encodeURIComponent(id)}`, data),
  deleteType: (id, opts) => api.delete(`/admin/venue-schema/types/${encodeURIComponent(id)}`, { params: opts }),
  getTypeVenueCount: (id) => api.get(`/admin/venue-schema/types/${encodeURIComponent(id)}/venue-count`),
  remap: (fromTypeId, toTypeId) => api.post('/admin/venue-schema/remap', { fromTypeId, toTypeId }),
  getUnclassified: (params) => api.get('/admin/venue-schema/unclassified', { params }),
  classifyVenue: (data) => api.post('/admin/venue-schema/classify-venue', data),
};

// ==== Translation CMS APIs ====
export const translationAPI = {
  getLocales: () => api.get('/api/i18n/locales'),
  getStats: () => api.get('/api/i18n/admin/translations/stats'),
  listTranslations: (params) => api.get('/api/i18n/admin/translations', { params }),
  updateTranslation: (id, data) => api.patch(`/api/i18n/admin/translations/${id}`, data),
  bulkApprove: (ids) => api.post('/api/i18n/admin/translations/bulk-approve', { ids }),
  getGlossary: () => api.get('/api/i18n/admin/glossary'),
  createGlossaryTerm: (data) => api.post('/api/i18n/admin/glossary', data),
  listCorrections: (params) => api.get('/api/i18n/admin/corrections', { params }),
  updateCorrection: (id, data) => api.patch(`/api/i18n/admin/corrections/${id}`, data),
  listJobs: () => api.get('/api/i18n/admin/translation-jobs'),
  createJob: (data) => api.post('/api/i18n/admin/translation-jobs', data),
};

// ==== User Admin APIs (ADMIN role required) ====
export const userAdminAPI = {
  getStats:         ()                  => api.get('/admin/users/stats'),
  listUsers:        (params)            => api.get('/admin/users', { params }),
  setUserRole:      (userId, role)      => api.patch(`/admin/users/${userId}/role`, { role }),
  setUserStatus:    (userId, status)    => api.patch(`/admin/users/${userId}/status`, { status }),
  setUserCurator:   (userId, isCurator) => api.patch(`/admin/users/${userId}/curator`, { is_curator: isCurator }), // EV-588
  deleteUser:       (userId)            => api.delete(`/admin/users/${userId}`),
  createUser:       (data)              => api.post('/admin/users/create', data),
};

// ==== FIELD-1626 — granular rights editor (admin.honestlee.app/permissions) ====
// Through the shared axios instance on purpose: a hand-written fetch('/api/...')
// on the admin host resolves to the SPA's index.html with HTTP 200 and silently
// yields {} — see the ADMIN LAW in CLAUDE.md.
export const permissionAPI = {
  getMatrix:    ()             => api.get('/admin/permissions'),
  getAudit:     (limit = 100)  => api.get('/admin/permissions/audit', { params: { limit } }),
  setRolePerms: (role, keys)   => api.put(`/admin/permissions/roles/${role}`, { keys }),
  setUserPerms: (userId, body) => api.put(`/admin/users/${userId}/permissions`, body),
};

// ==== Admin Preferences APIs ====
const ADMIN_PREF_HEADERS = { 'x-admin-key': 'honestlee-admin-2025' };
export const adminPreferencesAPI = {
  get: (key) => api.get(`/admin/preferences/${encodeURIComponent(key)}`, { headers: ADMIN_PREF_HEADERS }),
  set: (key, value) => api.put(`/admin/preferences/${encodeURIComponent(key)}`, { value }, { headers: ADMIN_PREF_HEADERS }),
};

// ==== Venue Notes Admin APIs ====
export const venueNotesAdminAPI = {
  // List all notes (admin view), with optional filters
  listNotes: (params = {}) => api.get('/admin/venue-notes', { params }),
  // Full-text search (sugar over listNotes with search param)
  searchNotes: (q, params = {}) => api.get('/admin/venue-notes', { params: { ...params, search: q } }),
  // Aggregate stats: totals, breakdown by type, top agents, top venues (filterable)
  getStats: (params = {}) => api.get('/admin/venue-notes/stats', { params }),
  // Distinct regions + cities among noted venues (for the filter dropdowns)
  getFilterOptions: () => api.get('/admin/venue-notes/filter-options'),
  // List all venues that have notes (for By Venue browser)
  getVenuesList: () => api.get('/admin/venue-notes/venues'),
  // Toggle pin on a note
  togglePin: (noteId) => api.post(`/admin/venue-notes/${noteId}/pin`),
  // Add emoji reaction to a note
  addReaction: (noteId, emoji) => api.post(`/admin/venue-notes/${noteId}/react`, { emoji }),
  // Admin delete a note (hard delete)
  deleteNote: (noteId) => api.delete(`/admin/venue-notes/${noteId}`),
};

// ==== Event Schema Manager APIs ====
export const eventSchemaAPI = {
  getTree:   ()            => api.get('/admin/event-schema/tree'),
  getFlat:   (level)       => api.get('/admin/event-schema/flat', { params: level !== undefined ? { level } : {} }),
  create:    (data)        => api.post('/admin/event-schema', data),
  update:    (slug, data)  => api.patch(`/admin/event-schema/${encodeURIComponent(slug)}`, data),
  delete:    (slug)        => api.delete(`/admin/event-schema/${encodeURIComponent(slug)}`),
  // EV-517 P2b — Field Designer (matrix grid + effective-config + attribute dictionary)
  getMatrix:        ()            => api.get('/admin/event-schema/matrix'),
  effectiveBatch:   (slugs)       => api.post('/admin/event-schema/effective-batch', { slugs }),
  // EV-517 P3 — resolved effective field config for one et_* leaf (dynamic forms).
  effectiveConfig:  (slug)        => api.get(`/admin/event-schema/${encodeURIComponent(slug)}/effective-config`),
  getAttributes:    ()            => api.get('/admin/event-attributes'),
  createAttribute:  (data)        => api.post('/admin/event-attributes', data),
  updateAttribute:  (key, data)   => api.patch(`/admin/event-attributes/${encodeURIComponent(key)}`, data),
  // EV-611 — Field Designer audit log + revert
  getAudit:         (params)      => api.get('/admin/event-schema/audit', { params: params || {} }),
  revertAudit:      (id)          => api.post(`/admin/event-schema/audit/${id}/revert`),
  // EV-615 — saved column/view presets
  getPresets:       ()           => api.get('/admin/event-schema/presets'),
  savePreset:       (name, payload) => api.post('/admin/event-schema/presets', { name, payload }),
  deletePreset:     (id)          => api.delete(`/admin/event-schema/presets/${id}`),
};

// ==== Events Manager APIs ====
export const eventsAdminAPI = {
  getStats:   ()           => api.get('/admin/events/stats'),
  getFacets:  ()           => api.get('/admin/events/facets'),         // v68.117
  getVenues:  (params)     => api.get('/admin/events/venues', { params }),
  getNearbyVenues: (lat, lng, radius = 1500) =>                              // v68.416 EV-03
    api.get('/admin/events/nearby-venues', { params: { lat, lng, radius } }),
  list:       (params)     => api.get('/admin/events', { params }),
  getById:    (id)         => api.get(`/admin/events/${id}`),
  getStops:   (id)         => api.get(`/admin/events/${id}/stops`),          // EV-804 multi-venue itinerary
  setStops:   (id, stops)  => api.put(`/admin/events/${id}/stops`, { stops }),
  getRsvps:   (id)         => api.get(`/admin/events/${id}/rsvps`),          // EV-804 who RSVP'd
  create:     (data)       => api.post('/admin/events', data),
  update:     (id, data)   => api.patch(`/admin/events/${id}`, data),
  deactivate: (id)         => api.delete(`/admin/events/${id}`),
  // v68.125 — photo poster → event candidate
  uploadPhoto: (file, ctx = {}) => {
    const fd = new FormData();
    fd.append('photo', file);
    if (ctx.city)     fd.append('city', ctx.city);
    if (ctx.region)   fd.append('region', ctx.region);
    if (ctx.venue_id) fd.append('venue_id', String(ctx.venue_id));
    return api.post('/admin/event-photo-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  // v68.138 — append an image directly to an existing event's gallery
  uploadImage: (eventId, file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return api.post(`/admin/events/${eventId}/upload-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  // v68.146 — server-side fetch a remote URL and re-host on our S3
  importImageUrl: (eventId, url) => api.post(`/admin/events/${eventId}/import-image-url`, { url }, { timeout: 60000 }),
  // v68.272 — paste raw text → AI extracts → review queue candidate
  createFromText: (payload) => api.post('/admin/event-candidates/from-text', payload, { timeout: 60000 }),
  // v68.281 — events.event_audit_log timeline for an event
  auditLog: (eventId) => api.get(`/admin/events/${eventId}/audit-log`),
  // v68.153 — WhatsApp thread timeline (via event id → first_seen_extracted_id → thread)
  timeline:    (eventId) => api.get(`/admin/events/${eventId}/timeline`),
  // EV-36 — convert a standalone event into an editioned series
  startSeries: (id, payload = {}) => api.post(`/admin/events/${id}/start-series`, payload),
  // canonical organizer picker (events.organizers)
  listOrganizers: () => api.get('/admin/organizers'),
  createOrganizer: (payload) => api.post('/admin/organizers', payload),
};

// ==== Venue Lifecycle Schema APIs ====
export const lifecycleAPI = {
  getStages:          ()          => api.get('/admin/lifecycle/stages'),
  updateStage:        (slug, data) => api.patch(`/admin/lifecycle/stages/${encodeURIComponent(slug)}`, data),
  getTransitions:     ()          => api.get('/admin/lifecycle/transitions'),
  createTransition:   (data)      => api.post('/admin/lifecycle/transitions', data),
  updateTransition:   (id, data)  => api.patch(`/admin/lifecycle/transitions/${id}`, data),
  deleteTransition:   (id)        => api.delete(`/admin/lifecycle/transitions/${id}`),
};

// ==== WiFi Speed Tests Admin APIs ====
export const wifiAdminAPI = {
  getStats:    ()       => api.get('/admin/wifi/stats'),
  listTests:   (params) => api.get('/admin/wifi/tests',    { params }),
  listByVenue: (params) => api.get('/admin/wifi/by-venue', { params }),
  adhocTest:   (data)   => api.post('/admin/wifi/adhoc-test', data),
  // v68.29: per-venue test source status — for admin map + APK map filter
  // GET /api/wifi-speed/venue-test-status?venueIds=1,2,3
  getVenueTestStatus: (venueIds) =>
    api.get('/wifi-speed/venue-test-status', { params: { venueIds: (venueIds || []).join(',') } }),
};

// ==== WiFi Networks Admin APIs (SSID layer) ====
export const wifiNetworkAdminAPI = {
  // Global networks list + stats
  listNetworks:        (params) => api.get('/admin/wifi/networks', { params }),
  getNetworkStats:     ()       => api.get('/admin/wifi/networks/stats'),
  getNetworkRegions:   ()       => api.get('/admin/wifi/networks/regions'),
  listNetworksByVenue: (params) => api.get('/admin/wifi/networks/by-venue', { params }),
  // Per-venue operations
  listVenueNetworks:   (venueId) => api.get(`/admin/wifi/venues/${venueId}/networks`),
  saveNetwork:         (venueId, data) => api.post(`/admin/wifi/venues/${venueId}/networks`, data),
  updateNetwork:       (id, data) => api.patch(`/admin/wifi/networks/${id}`, data),
  deleteNetwork:       (id)      => api.delete(`/admin/wifi/networks/${id}`),
  // v66.156 (2026-05-18): canonical promotion + non-canonical venue listing.
  // Pair with backend v68.61. Promote fills bssid+password+verified so
  // wifi_networks.is_canonical generated column flips true.
  promoteToCanonical:  (id, body) => api.patch(`/admin/wifi/networks/${id}/promote`, body),
  listNonCanonicalVenues: (params) => api.get('/admin/wifi/networks/non-canonical-venues', { params }),
  // Displays
  listVenueDisplays:   (venueId) => api.get(`/admin/wifi/venues/${venueId}/displays`),
  saveDisplay:         (venueId, data) => api.post(`/admin/wifi/venues/${venueId}/displays`, data),
  deleteDisplay:       (id)      => api.delete(`/admin/wifi/displays/${id}`),
  // Lookup tables
  getMethodTypes:      ()        => api.get('/admin/wifi/method-types'),
  getLocationTypes:    ()        => api.get('/admin/wifi/location-types'),
  // Backfill
  getUnlinkedTests:    (params)  => api.get('/admin/wifi/backfill/unlinked-tests', { params }),
  backfillFromSpeedTests: ()     => api.post('/admin/wifi/backfill/from-speed-tests'),
  relinkSingleSsidVenues: ()    => api.post('/admin/wifi/backfill/relink-single-ssid'),
  linkTests:           (data)    => api.post('/admin/wifi/backfill/link-tests', data),
  getNotesHints:       ()        => api.get('/admin/wifi/backfill/notes-hints'),
  getPhotoHints:       ()        => api.get('/admin/wifi/backfill/photo-hints'),
  getWifiSignHints:    ()        => api.get('/admin/wifi/backfill/wifi-sign-hints'),
  listCredentialEvents: (params) => api.get('/admin/wifi/credential-events', { params }),
  runNotesExtraction:  (data)    => api.post('/admin/wifi/backfill/from-notes', data),
  runPhotoExtraction:  (data)    => api.post('/admin/wifi/backfill/from-photos', data),
  runWifiSignScan:     (data)    => api.post('/admin/wifi/backfill/wifi-sign-scan', data),
  applyBackfill:       (data)    => api.post('/admin/wifi/backfill/apply', data),
  // Fuzzy duplicate detection + merge
  findFuzzyDuplicates: (threshold = 2) => api.get('/admin/wifi/networks/fuzzy-duplicates', { params: { threshold } }),
  mergeNetworks:       (data)    => api.post('/admin/wifi/networks/merge', data),
  getVenueHeatmap:     (venueId, type = 'all') => api.get(`/admin/wifi/speed/venues/${venueId}/heatmap`, { params: { type } }),
  getGlobalHeatmap:    (type = 'all', limit = 8000) => api.get('/admin/wifi/speed/global-heatmap', { params: { type, limit } }),
  getGlobalBoundaries: () => api.get('/admin/wifi/speed/global-boundaries'),
};

// ==== WM Photos Extraction APIs ====
export const wmPhotosAPI = {
  // Upload images + extract via LLM (multipart/form-data, field name: "images")
  extract: (formData, model) => api.post('/admin/wifi/wm-photos/extract', formData, {
    headers: { 'Content-Type': 'multipart/form-data', 'x-admin-key': 'honestlee-admin-2025' },
    params: { model },
    timeout: 120000,  // 2 min timeout for large batches
  }),
  // Save extracted venues to wm_hotspots staging table
  apply: (data) => api.post('/admin/wifi/wm-photos/apply', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // Match staged records against existing wifi_networks
  match: (data) => api.post('/admin/wifi/wm-photos/match', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // Push selected records to wifi_networks (user-confirmed)
  pushSelected: (data) => api.post('/admin/wifi/wm-photos/push-selected', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // List all wm_hotspots records
  list: (params) => api.get('/admin/wifi/wm-photos/list', {
    params,
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // Delete a single record
  remove: (id) => api.delete(`/admin/wifi/wm-photos/${id}`, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // Resolve addresses via Google Places (Phase 2 — separate from extraction)
  resolveAddresses: (data) => api.post('/admin/wifi/wm-photos/resolve-addresses', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
    timeout: 600000,  // 10 min — parallel batches of 10, ~3 min for 180 venues
  }),
  // Create venue prospects in Neon venues table for unmatched wm_hotspots
  createProspects: (data) => api.post('/admin/wifi/wm-photos/create-prospects', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
    timeout: 300000,  // 5 min — can create 100+ venues with wifi_networks + speed tests
  }),
  // Upload-first pipeline: Step 1 — upload images to S3, no LLM (with upload progress support)
  uploadBatch: (formData, { onUploadProgress } = {}) => api.post('/admin/wifi/wm-photos/upload-batch', formData, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
    timeout: 300000, // 5 min for large batches
    onUploadProgress,
  }),
  // Upload-first pipeline: Step 1b — get count of unprocessed uploads
  pendingImages: (params = {}) => api.get('/admin/wifi/wm-photos/pending-images', {
    params: { limit: 500, ...params },
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  // Upload-first pipeline: Step 2 — process a batch of upload IDs via LLM
  processBatch: (data) => api.post('/admin/wifi/wm-photos/process-batch', data, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
    timeout: 120000,
  }),
  dedup: () => api.post('/admin/wifi/wm-photos/dedup', {}, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
    timeout: 60000,
  }),
  deleteMany: (ids) => api.post('/admin/wifi/wm-photos/delete-many', { ids }, {
    headers: { 'x-admin-key': 'honestlee-admin-2025' },
  }),
  getPresets:   ()           => api.get('/admin/wifi/wm-photos/presets', { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  savePreset:   (data)       => api.post('/admin/wifi/wm-photos/presets', data, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  deletePreset: (name)       => api.delete(`/admin/wifi/wm-photos/presets/${encodeURIComponent(name)}`, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  // Batch management
  listBatches:         ()           => api.get('/admin/wifi/wm-photos/batches', { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  createBatch:         (data)       => api.post('/admin/wifi/wm-photos/batches', data, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  patchBatch:          (id, data)   => api.patch(`/admin/wifi/wm-photos/batches/${id}`, data, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  claimUploadsToBatch: (id)         => api.patch(`/admin/wifi/wm-photos/batches/${id}/claim-uploads`, {}, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
  setIgnored:          (ids, ignored) => api.patch('/admin/wifi/wm-photos/set-ignored', { ids, ignored }, { headers: { 'x-admin-key': 'honestlee-admin-2025' } }),
};

// ==== Deploy Control Panel APIs ====
export const deployAPI = {
  getDeployStatus:  ()                          => api.get('/admin/deploy/status'),
  triggerDeploy:    (component, environment)    => api.post('/admin/deploy/trigger', { component, environment }),
  getDeployLogs:    (deployId)                  => api.get(`/admin/deploy/logs/${deployId}`),
  getDeployHistory: ()                          => api.get('/admin/deploy/history'),
  getDeployPM2:     ()                          => api.get('/admin/deploy/pm2'),
  getDeployHealth:  ()                          => api.get('/admin/deploy/health'),
  getEnvironmentHealth: ()                      => api.get('/admin/deploy/environment-health'),
};

// ==== AI / OCR Prompt Manager APIs ====
export const aiPromptAPI = {
  list:      ()              => api.get('/admin/ai/ocr-prompts'),
  get:       (slug)          => api.get(`/admin/ai/ocr-prompts/${slug}`),
  update:    (slug, data)    => api.patch(`/admin/ai/ocr-prompts/${slug}`, data),
};

// ==== ASN Registry Admin APIs ====
export const asnRegistryAPI = {
  getStats:   ()            => api.get('/admin/asn/stats'),
  list:       (params)      => api.get('/admin/asn/registry', { params }),
  update:     (asNum, data) => api.patch(`/admin/asn/${asNum}`, data),
};

// ==== User APIs (Public) ====
export const userAPI = {
  getVenueDetails: (venueId) => axios.get(`${API_BASE_URL}/venues/${venueId}`),
  submitSpeedTestPublic: (data) =>
    axios.post(`${API_BASE_URL}/wifi-speed/test`, data, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    }),
  getVenueSpeedTestsPublic: (venueId) =>
    axios.get(`${API_BASE_URL}/wifi-speed/venue/${venueId}`),
};

export default api;

// ===== Digital Menu Manager =====
// ==== Reviews Admin APIs ====
export const reviewsAdminAPI = {
  listReviews:       (params) => api.get('/admin/reviews', { params }),
  updateModeration:  (id, status) => api.put(`/admin/reviews/${id}/moderation`, { status }),
  deleteReview:      (id) => api.delete(`/admin/reviews/${id}`, { data: { confirm: true } }),
};

export const menuAPI = {
  // Stats
  getStats: () => api.get('/admin/menu/stats'),
  // Venue list (venues with menu images)
  getVenues: (params) => api.get('/admin/menu/venues', { params }),
  // Unified per-venue source breakdown (grab_delivery + google_maps + agent_upload + google_photo_ocr)
  getSourcesOverview: (params) => api.get('/admin/menu/sources-overview', { params }),
  // Distinct regions + cities for populating dropdowns (only rows that have data)
  getSourceFilters: () => api.get('/admin/menu/source-filters'),
  getVenueImages: (venueId) => api.get(`/admin/menu/venues/${venueId}/images`),
  getVenueDetail: (venueId) => api.get(`/admin/menu/venues/${venueId}/detail`),
  // Structured menus
  listMenus: (params) => api.get('/admin/menu/menus', { params }),
  getMenu: (menuId) => api.get(`/admin/menu/menus/${menuId}`),
  deleteMenu: (menuId) => api.delete(`/admin/menu/menus/${menuId}`),
  updateMenuStatus: (menuId, status, notes) => api.patch(`/admin/menu/menus/${menuId}/status`, { status, notes }),
  // Available models
  getModels: () => api.get('/admin/menu/models'),
  // Extraction jobs
  listJobs: (params) => api.get('/admin/menu/jobs', { params }),
  createJob: (data) => api.post('/admin/menu/jobs', data),
  startExtraction: (data) => api.post('/admin/menu/jobs/start', data, { timeout: 300000 }),
  getJob: (jobId) => api.get(`/admin/menu/jobs/${jobId}`),
  runJob: (jobId) => api.post(`/admin/menu/jobs/${jobId}/run`),
  deleteJob: (jobId) => api.delete(`/admin/menu/jobs/${jobId}`),
  // Prompts
  listPrompts: (params) => api.get('/admin/menu/prompts', { params }),
  createPrompt: (data) => api.post('/admin/menu/prompts', data),
  getPrompt: (promptId) => api.get(`/admin/menu/prompts/${promptId}`),
  updatePrompt: (promptId, data) => api.put(`/admin/menu/prompts/${promptId}`, data),
  activatePrompt: (promptId) => api.put(`/admin/menu/prompts/${promptId}/activate`),
  togglePrompt: (promptId) => api.post(`/admin/menu/prompts/${promptId}/toggle`),
};

// ==== Venue Google Data Admin APIs ====
export const venueGoogleDataAPI = {
  list:  (params) => api.get('/admin/venue-google-data', { params }),
  stats: ()       => api.get('/admin/venue-google-data/stats'),
};

export const venueGoogleEnrichmentAPI = {
  list:    (params) => api.get('/admin/venue-google-enrichment', { params }),
  stats:   ()       => api.get('/admin/venue-google-enrichment/stats'),
  columns: ()       => api.get('/admin/venue-google-enrichment/columns'),
  getById: (id)     => api.get(`/admin/venue-google-enrichment/${id}`),
};

// ==== LINE Inbox APIs ====
export const lineInboxAPI = {
  getInbox:         (params) => api.get('/admin/line-inbox', { params }),
  getMessages:      (lineUserId) => api.get(`/admin/line-inbox/${lineUserId}/messages`),
  sendReply:        (data) => api.post('/admin/line-reply', data),
};

// ==== Venue Invite APIs ====
export const venueInviteAPI = {
  createInvite:     (venueId, data) => api.post(`/admin/venues/${venueId}/invite`, data || { role: 'OWNER' }),
  getStaff:         (venueId) => api.get(`/admin/venues/${venueId}/staff`),
};

// ==== WhatsApp Intelligence Pipeline APIs ====
export const whatsappPipelineAPI = {
  // BE-1397 — MULTI-ACCOUNT. One row per Baileys session / phone number.
  // Every list call below also accepts { account_id } in params; omitting it
  // means ALL accounts, which is the pre-existing behaviour.
  getAccounts:       ()       => api.get('/whatsapp/accounts'),
  updateAccount:     (id, data) => api.patch(`/whatsapp/accounts/${id}`, data),
  getAccountConversations: (params) => api.get('/whatsapp/accounts/conversations', { params }),
  // AD-1399 — Direct Messages tab
  getDirectThread:   (params) => api.get('/whatsapp/dm/thread', { params }),
  setDirectTracking: (body)   => api.post('/whatsapp/dm/track', body),
  // AD-1399 v69.982 — classify a chat inline (venue + tags + about), no CRM detour
  getDmTags:         ()       => api.get('/whatsapp/dm/tags'),
  setDmEnrichment:   (body)   => api.post('/whatsapp/dm/enrich', body),
  // Stats
  getStats:          (params) => api.get('/whatsapp/stats', { params }),
  // Contacts
  getContacts:       (params) => api.get('/whatsapp/contacts', { params }),
  getContactCountries: (params) => api.get('/whatsapp/contacts/countries', { params }),  // BE-1397 country facets
  getContactDetail:  (id)     => api.get(`/whatsapp/contacts/${id}/detail`),                    // EV-1095
  pasteContactTranscript: (id, text) => api.post(`/whatsapp/contacts/${id}/paste-transcript`, { text }), // EV-1277
  getContactMessages:(id, params) => api.get(`/whatsapp/contacts/${id}/messages`, { params }),  // EV-1095
  updateContact:     (id, data) => api.patch(`/whatsapp/contacts/${id}`, data),
  // Groups
  getGroups:         (params) => api.get('/whatsapp/groups', { params }),
  getGroupsTree:     (params) => api.get('/whatsapp/groups/tree', { params }),   // BE-1397 — accepts account_id
  getGroupGraph:     (params) => api.get('/whatsapp/graph', { params }),  // shared-member force graph
  refreshFromWhatsApp: ()     => fetch('https://core.honestlee.app/wa/api/groups/refresh', { method: 'POST' }).then(r => r.json()),
  // Presets
  getPresets:         ()       => api.get('/whatsapp/presets'),
  savePreset:         (name, filters) => api.post('/whatsapp/presets', { name, filters }),
  deletePreset:       (id)     => api.delete(`/whatsapp/presets/${id}`),
  setDefaultPreset:   (id)     => api.post(`/whatsapp/presets/${id}/default`),
  updateGroup:       (id, data) => api.patch(`/whatsapp/groups/${id}`, data),
  // Messages
  getMessages:       (params) => api.get('/whatsapp/messages', { params }),
  // Events
  getEvents:         (params) => api.get('/whatsapp/events', { params }),
  updateEvent:       (id, data) => api.patch(`/whatsapp/events/${id}`, data),
  approveEvent:      (id)     => api.post(`/whatsapp/events/${id}/approve`),
  rejectEvent:       (id)     => api.post(`/whatsapp/events/${id}/reject`),
  linkVenue:         (id, venueId) => api.post(`/whatsapp/events/${id}/link-venue`, { venueId }),
  // Extraction
  extractMessage:    (id, model) => api.post(`/whatsapp/extract/message/${id}`, { model }),
  runExtraction:     (data)   => api.post('/whatsapp/extract/run', data),
  getExtractionStats:()       => api.get('/whatsapp/extract/stats'),
  getExtractionModels:()      => api.get('/whatsapp/extract/models'),
  // v68.103 — filtered pool stats for /event-intelligence Batch Runner
  getExtractionPool: (params) => api.get('/whatsapp/extract/pool', { params }),
  getRecentRuns:     (limit)  => api.get('/whatsapp/extract/recent-runs', { params: { limit } }),
  // v68.104 — preview the N messages that would be sent to the LLM if you Run now
  getExtractionPreview: (params) => api.get('/whatsapp/extract/preview', { params }),
  // v68.106 — thread-based pipeline (HL-CP-0303 4-tier design)
  getThreadPool:     (params) => api.get('/whatsapp/extract/threads/pool', { params }),
  getThreadPreview:  (params) => api.get('/whatsapp/extract/threads/preview', { params }),
  // v68.110 — paginated browse (replaces preview for rich table)
  browseThreads:     (params) => api.get('/whatsapp/extract/threads/browse', { params }),
  runThreadBatch:    (data)   => api.post('/whatsapp/extract/threads/run', data),
  extractThread:     (id, body) => api.post(`/whatsapp/extract/threads/${id}`, body || {}),
  // v68.107 — fetch full thread with all messages for the row-expand view
  getThreadMessages: (id)      => api.get(`/whatsapp/extract/threads/${id}/messages`),
  // Matching
  matchEvent:        (id)     => api.post(`/whatsapp/match/event/${id}`),
  matchAll:          ()       => api.post('/whatsapp/match/run'),
  searchVenues:      (q)      => api.get('/whatsapp/match/search-venues', { params: { q } }),
  // Places
  getPlaces:         (params) => api.get('/whatsapp/places', { params }),
  getPlacesSummary:  ()       => api.get('/whatsapp/places/summary'),
  resolvePlaces:     (limit)  => api.post('/whatsapp/places/resolve', { limit }),
  getResolvedPlaces: (params) => api.get('/whatsapp/places/resolved', { params }),
  addProspects:      (placeIds, lifecycleStage) => api.post('/whatsapp/places/add-prospects', { placeIds, lifecycleStage }),
};

// v68.114 — Server-side saved views (column visibility, sort, filters, etc)
export const screenPresetsAPI = {
  list:    (screen_key) => api.get('/admin/screen-presets', { params: { screen_key } }),
  save:    (data)       => api.post('/admin/screen-presets', data),
  delete:  (id)         => api.delete(`/admin/screen-presets/${id}`),
};

// v68.109 — Event candidates (Review queue, Publish to events.events)
// v68.115 — venue resolve + suggestions + create
export const eventCandidatesAPI = {
  list:               (params) => api.get('/admin/event-candidates', { params }),
  stats:              ()       => api.get('/admin/event-candidates/stats'),
  update:             (id, data) => api.patch(`/admin/event-candidates/${id}`, data),
  reject:             (id)     => api.post(`/admin/event-candidates/${id}/reject`),
  publish:            (id)     => api.post(`/admin/event-candidates/${id}/publish`),
  bulk:               (data)   => api.post('/admin/event-candidates/bulk', data),
  resolveVenue:       (id)     => api.post(`/admin/event-candidates/${id}/resolve-venue`),
  venueSuggestions:   (id, limit = 8) => api.get(`/admin/event-candidates/${id}/venue-suggestions`, { params: { limit } }),
  createVenue:        (id, body) => api.post(`/admin/event-candidates/${id}/create-venue`, body),
  // v68.128 — URL scraper (Tier 1-3)
  scrapeUrls:         (id, urls) => api.post(`/admin/event-candidates/${id}/scrape-urls`, urls ? { urls } : {}),
  bulkScrapeUrls:     (ids)     => api.post('/admin/event-candidates/bulk-scrape-urls', { ids }),
  // v68.153 — WhatsApp thread timeline (per-message, sender, media)
  timeline:           (id)      => api.get(`/admin/event-candidates/${id}/timeline`),
};

// ── PWA WiFi Gate Config (v68.52, 2026-05-17) ─────────────────────────────────
// Tunes the consumer PWA map drawer Connect-to-WiFi gating:
//   radius_m, per-platform enabled, per-platform show-password-inline, fail_mode.
// Reads + writes a single row in system.config (backend v68.50).
export const wifiGateConfigAPI = {
  get:    ()        => api.get('/admin/wifi-gate-config'),
  patch:  (payload) => api.patch('/admin/wifi-gate-config', payload),
};

// AD-47 — event-detail carousel tuning (nearby radius / similar tightness / limits)
export const carouselConfigAPI = {
  get:  ()        => api.get('/admin/carousel-config'),
  put:  (payload) => api.put('/admin/carousel-config', payload),
};

// v68.371 — Google Drive ingest admin panel
export const driveIngestAPI = {
  getConfig:  ()      => api.get('/admin/drive-ingest/config'),
  saveConfig: (body)  => api.post('/admin/drive-ingest/config', body),
  test:       (body)  => api.post('/admin/drive-ingest/test', body || {}),
  run:        (body)  => api.post('/admin/drive-ingest/run', body || {}),
};

// v68.372 — in-house visitor analytics dashboard
// v68.779 — extended with sessions + session-detail endpoints
export const analyticsAPI = {
  overview:  (params)    => api.get('/admin/analytics/overview', { params }),
  sessions:  (params)    => api.get('/admin/analytics/sessions', { params }),
  session:   (sessionId) => api.get(`/admin/analytics/sessions/${sessionId}`),
};
